import React, { useState, useEffect, useCallback } from 'react';
import { Package, AlertCircle, RefreshCcw, Clock, CheckCircle, XCircle, MapPin, ChevronRight, FileCheck, Inbox } from 'lucide-react';
import { Button } from 'primereact/button';
import ProjectApprovalDrawer from '../components/ProjectApprovalDrawer';
import AmsAuditApprovalList from '../components/AmsAuditApprovalList';
import AmsAuditApprovalDrawer from '../components/AmsAuditApprovalDrawer';
import {
  ApprovalRequest,
  getPendingApprovals,
} from '../../../services/approvalService';
import { AmsAuditApproval } from '../../../services/amsAuditApprovalService';
import type { AmsProjectAudit } from '../../../services/amsProjectAuditService';
import { resolveAmsProjectId } from '../utils/resolveAmsProjectId';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { format, formatDistanceToNow } from 'date-fns';

function getProjectLocationKey(request: ApprovalRequest): string {
  const loc = (request.requestData || {}).location;
  if (!loc) return '__no_location__';
  if (typeof loc === 'string') return loc;
  const o = loc as { _id?: string; name?: string };
  if (o._id) return o._id;
  if (o.name) return String(o.name);
  return '__no_location__';
}

function getProjectLocationDisplayName(request: ApprovalRequest): string {
  const loc = (request.requestData || {}).location;
  if (!loc) return 'No location';
  if (typeof loc === 'string') return loc;
  const o = loc as { name?: string };
  return o.name || 'Unknown location';
}

interface ProjectApprovalCardProps {
  request: ApprovalRequest;
  onClick: () => void;
  /** Optional delay (ms) for staggered entrance animation */
  animationDelay?: number;
}

const ProjectApprovalCard: React.FC<ProjectApprovalCardProps> = ({ request, onClick, animationDelay = 0 }) => {
  const requestData = request.requestData || {};
  const approvalStatus = requestData.approvalStatus || request.status;
  const projectLevelStatus = requestData.projectLevelApprovalStatus;
  const isResubmitted = approvalStatus === 'pending_approval' &&
    (requestData.rejectionReason || requestData.projectLevelApproverComments);
  const isPartiallyApproved = approvalStatus === 'partially_approved';
  const isPending = request.status === 'pending' || approvalStatus === 'pending_approval';

  const getStatusConfig = () => {
    if (isResubmitted)
      return { label: 'Resubmitted', severity: 'warning' as const, Icon: RefreshCcw };
    if (isPartiallyApproved)
      return { label: 'Partially approved', severity: 'info' as const, Icon: AlertCircle };
    if (isPending)
      return { label: 'Pending', severity: 'warning' as const, Icon: Clock };
    return request.status === 'approved'
      ? { label: 'Approved', severity: 'success' as const, Icon: CheckCircle }
      : { label: 'Rejected', severity: 'danger' as const, Icon: XCircle };
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.Icon;

  const cardTheme = request.status === 'approved'
    ? {
        accent: 'bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600',
        accentBorder: 'border-emerald-400/90',
        strip: 'bg-emerald-50/40',
        statusBg: 'bg-emerald-100 text-emerald-800',
        iconColor: 'text-white',
      }
    : request.status === 'rejected'
      ? {
          accent: 'bg-gradient-to-br from-rose-400 via-rose-500 to-rose-600',
          accentBorder: 'border-rose-400/90',
          strip: 'bg-rose-50/30',
          statusBg: 'bg-rose-100 text-rose-800',
          iconColor: 'text-white',
        }
      : {
          accent: 'bg-gradient-to-br from-amber-300 via-amber-400 to-amber-500',
          accentBorder: 'border-amber-400/90',
          strip: 'bg-amber-50/50',
          statusBg: 'bg-amber-100 text-amber-800',
          iconColor: 'text-amber-950',
        };

  const getStageLine = () => {
    if (isResubmitted) return 'Resubmitted for review';
    if (isPartiallyApproved) {
      const parts: string[] = [];
      if (projectLevelStatus === 'pending') parts.push('Project');
      if (requestData.pendingAssetsCount > 0) parts.push(`${requestData.pendingAssetsCount} asset(s)`);
      if (requestData.pendingCheckpointsCount > 0) parts.push(`${requestData.pendingCheckpointsCount} checkpoint(s)`);
      if (requestData.pendingResourcesCount > 0) parts.push(`${requestData.pendingResourcesCount} resource(s)`);
      return parts.length > 0 ? parts.join(', ') : 'Some components pending';
    }
    if (projectLevelStatus === 'pending') return 'Project level pending';
    return 'Awaiting approval';
  };

  const comments = request.approverComments || requestData.rejectionReason || requestData.projectLevelApproverComments;
  const assets = requestData.totalAssets ?? 0;
  const equipment = requestData.totalEquipment ?? 0;
  const checkpoints = requestData.totalCheckpoints ?? 0;

  // Manager's resubmission text (detailed comment when resubmitting for review)
  const resubmissionText =
    request.metadata?.submissionComments ??
    request.metadata?.resubmissionNotes ??
    requestData.resubmissionNotes ??
    requestData.submissionComments;

  const projectNumber = requestData.projectId || request.resourceId;
  const projectName = requestData.projectName || 'Project';
  const startDate = requestData.startDate ? format(new Date(requestData.startDate), 'MMM d, yyyy') : null;
  const endDate = requestData.endDate ? format(new Date(requestData.endDate), 'MMM d, yyyy') : null;

  const sentForReviewDate = request.createdAt
    ? format(new Date(request.createdAt), 'MMM d, yyyy • HH:mm')
    : null;
  const daysAgoText = request.createdAt
    ? formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })
    : null;

  const isPendingState = isPending || isResubmitted || isPartiallyApproved;
  const locationName = requestData.location
    ? (typeof requestData.location === 'object' && requestData.location?.name
        ? requestData.location.name
        : String(requestData.location))
    : null;
  const dateRange = startDate && endDate ? `${startDate} – ${endDate}` : startDate || endDate;
  const summaryParts: string[] = [];
  if (locationName) summaryParts.push(locationName);
  summaryParts.push(`Requester: ${request.requesterName}`);
  summaryParts.push(getStageLine());
  if (dateRange) summaryParts.push(dateRange);
  if (daysAgoText) summaryParts.push(`Sent ${daysAgoText}`);
  const summaryText = summaryParts.join(' · ');

  return (
    <div
      className="flex flex-col gap-1.5 opacity-0 animate-card-reveal"
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        className="group flex flex-row items-stretch font-[family-name:var(--font-outfit)] text-left cursor-pointer rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.995]"
      >
        {/* Left: Status tab with arrow (overlaps middle) — infographic-style */}
        <div className="relative z-10 flex-shrink-0 w-24 sm:w-28 min-h-[100px] sm:min-h-[116px] flex flex-col items-center justify-center pr-2 sm:pr-3">
          <div
            className={`absolute inset-0 rounded-l-2xl ${cardTheme.accent} transition-all duration-300 group-hover:brightness-105`}
            style={{
              clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)',
              boxShadow: '2px 0 10px rgba(0,0,0,0.06)',
            }}
          />
          <div className="relative z-10 flex flex-col items-center justify-center text-center px-1">
            <span className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider ${cardTheme.iconColor} opacity-95 leading-tight line-clamp-2`}>
              {statusConfig.label}
            </span>
            <span className={`text-lg sm:text-xl font-bold mt-1 tabular-nums ${cardTheme.iconColor} truncate max-w-full`} title={projectNumber}>
              {projectNumber.length > 6 ? `#${String(projectNumber).slice(-4)}` : projectNumber}
            </span>
          </div>
        </div>

        {/* Middle: Cream content area (layered behind left, overlaps right) */}
        <div
          className="relative z-0 flex-1 min-w-0 -ml-3 sm:-ml-4 flex flex-col justify-center py-4 pl-6 sm:pl-8 pr-4 sm:pr-6 rounded-xl bg-gradient-to-br from-amber-50/95 via-stone-50/90 to-amber-50/80 border border-amber-100/80 transition-all duration-300"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-sm sm:text-base font-bold text-stone-800 uppercase tracking-wide truncate border-b border-stone-200/80 pb-1.5">
            {projectName}
          </h3>
          <p className="mt-2 text-xs sm:text-sm text-stone-600 leading-snug line-clamp-2 sm:line-clamp-3">
            {summaryText}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-stone-500 tabular-nums">
              {format(new Date(request.createdAt), 'MMM d, yyyy')}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-stone-500 group-hover:text-blue-600 transition-colors duration-200 ml-auto">
              {isPendingState ? 'Review' : 'View'}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </span>
          </div>
        </div>

        {/* Right: White icon panel (overlaps middle) */}
        <div
          className="relative z-10 flex-shrink-0 w-14 sm:w-16 min-h-[100px] sm:min-h-[116px] -ml-2 flex items-center justify-center rounded-r-2xl bg-white border border-l-0 border-stone-200/60 transition-all duration-300 group-hover:shadow-md"
          style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}
        >
          <StatusIcon className={`w-7 h-7 sm:w-8 sm:h-8 text-stone-400 transition-transform duration-300 group-hover:scale-110 group-hover:text-stone-600`} strokeWidth={1.6} />
        </div>
      </div>

      {/* Resubmission note — below card */}
      {resubmissionText && (isResubmitted || isPending) && (
        <div className="px-4 py-3 rounded-xl border border-amber-200/80 bg-gradient-to-r from-amber-50/90 to-amber-100/50 shadow-sm transition-shadow duration-200">
          <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wider mb-1">
            Manager&apos;s resubmission note
          </p>
          <p className="text-xs text-slate-800 leading-snug">{resubmissionText}</p>
        </div>
      )}

      {/* Comments — below card */}
      {comments && (
        <div className="px-4 py-3 rounded-xl bg-gradient-to-r from-amber-50/80 to-amber-100/40 border border-amber-100 shadow-sm transition-shadow duration-200">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wider mb-0.5">Comments</p>
              <p className="text-xs text-slate-700 leading-snug">{comments}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectApprovals: React.FC = () => {
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [projectApprovalOpen, setProjectApprovalOpen] = useState(false);
  
  // AMS Project Approval state
  const [selectedAmsProjectId, setSelectedAmsProjectId] = useState<string | null>(null);
  const [selectedAmsAuditId, setSelectedAmsAuditId] = useState<string | null>(null);
  const [amsProjectApprovalOpen, setAmsProjectApprovalOpen] = useState(false);
  const [amsRefreshSignal, setAmsRefreshSignal] = useState(0);

  const { user, activeDepartment } = useAuth();
  const isApprover = user?.role?.roleType === 'approver' || user?.role?.roleType === 'both';
  
  // AMS gets its own audit-approval surface; every other department (OMS, EPC)
  // sees the standard pending-projects review page.
  const showAmsView = activeDepartment === 'AMS';

  const loadPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPendingApprovals({ page: 1, limit: 50 });
      const projectApprovals = (response.data || [])
        .filter((req: ApprovalRequest) => req.resourceType === 'project')
        .filter((req: ApprovalRequest) => {
          const requestData = req.requestData || {};
          const approvalStatus = requestData.approvalStatus || req.status;
          return (
            req.status === 'pending' ||
            approvalStatus === 'pending_approval' ||
            approvalStatus === 'partially_approved' ||
            (approvalStatus === 'pending_approval' &&
              (requestData.rejectionReason || requestData.projectLevelApproverComments))
          );
        });
      setPendingApprovals(projectApprovals);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isApprover) loadPendingApprovals();
  }, [isApprover, loadPendingApprovals]);

  const handleProjectClick = (request: ApprovalRequest) => {
    if (request.resourceId) {
      setSelectedProjectId(request.resourceId);
      setProjectApprovalOpen(true);
    }
  };

  const handleAmsAuditClick = (approval: AmsAuditApproval, audit: AmsProjectAudit | null) => {
    const projectId = resolveAmsProjectId(audit?.amsProjectId);

    if (!projectId) {
      toast.error('Unable to open this audit — its project could not be resolved.');
      return;
    }

    setSelectedAmsProjectId(projectId);
    setSelectedAmsAuditId(approval.amsProjectAuditId);
    setAmsProjectApprovalOpen(true);
  };

  const pendingCount = pendingApprovals.length;

  return (
    <div className="w-full min-h-screen px-4 sm:px-6 lg:px-8 py-6 font-[family-name:var(--font-outfit)]">
      {showAmsView ? (
        <>
          {/* AMS: department-scoped audit approvals (the list refreshes itself) */}
          <div className="mb-6">
            <h1 className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight">
              Project approvals
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Review and approve AMS audit submissions
            </p>
          </div>
          <section className="rounded-2xl border border-slate-200/60 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="p-4 sm:p-5">
              <AmsAuditApprovalList onAuditClick={handleAmsAuditClick} refreshSignal={amsRefreshSignal} />
            </div>
          </section>
        </>
      ) : (
        <>
      {/* Pending summary hero */}
      <div className="mb-6 rounded-2xl border border-slate-200/60 bg-gradient-to-br from-slate-50/90 via-white to-amber-50/40 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-300 hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
        <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200/80 border border-amber-200/50 flex items-center justify-center flex-shrink-0 shadow-sm transition-transform duration-300 hover:scale-105">
              <FileCheck className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight">
                Project approvals pending
              </h1>
              <p className="text-sm text-slate-600 mt-0.5 max-w-md">
                {loading
                  ? 'Loading…'
                  : pendingCount === 0
                    ? 'No projects awaiting your review right now.'
                    : `${pendingCount} project${pendingCount !== 1 ? 's' : ''} awaiting your review.`}
              </p>
            </div>
          </div>
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            className="p-button-outlined p-button-sm shrink-0"
            loading={loading}
            onClick={loadPendingApprovals}
          />
        </div>
        {!loading && pendingCount > 0 && (
          <div className="px-5 sm:px-6 py-2.5 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2 text-xs text-slate-600">
            <Clock className="w-3.5 h-3.5 text-amber-500" />
            <span>Click a project to review and approve or reject.</span>
          </div>
        )}
      </div>

      {/* Main content */}
      <section className="rounded-2xl border border-slate-200/60 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-300">
        <div className="px-4 sm:px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 bg-gradient-to-r from-slate-50/50 to-transparent">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Inbox className="w-4 h-4 text-slate-500" />
            Pending review
            {!loading && pendingCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-gradient-to-r from-amber-100 to-amber-200/80 text-amber-800 text-xs font-medium border border-amber-200/50 transition-transform duration-200 hover:scale-105">
                {pendingCount}
              </span>
            )}
          </h2>
        </div>
        <div className="p-4 sm:p-5 min-h-[200px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-9 h-9 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
              <p className="mt-3 text-sm font-medium text-slate-500">Loading approvals…</p>
            </div>
          ) : pendingCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center max-w-sm mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-slate-400" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">No project approvals pending</h3>
              <p className="text-sm text-slate-500">
                All project requests have been reviewed. New submissions will appear here when they need your approval.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              {(() => {
                const byLocation: Record<string, ApprovalRequest[]> = {};
                pendingApprovals.forEach((request) => {
                  const key = getProjectLocationKey(request);
                  if (!byLocation[key]) byLocation[key] = [];
                  byLocation[key].push(request);
                });
                return Object.entries(byLocation).map(([locationKey, requests]) => (
                  <div key={locationKey} className="flex flex-col gap-4">
                    <div className="flex items-center gap-3 px-1">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200/80 flex items-center justify-center flex-shrink-0 border border-blue-200/60 transition-transform duration-200 hover:scale-105">
                        <MapPin className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {getProjectLocationDisplayName(requests[0]!)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {requests.length} project{requests.length !== 1 ? 's' : ''} at this location
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      {requests.map((request, idx) => (
                        <ProjectApprovalCard
                          key={request._id}
                          request={request}
                          onClick={() => handleProjectClick(request)}
                          animationDelay={idx * 60}
                        />
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </section>
        </>
      )}

      {/* OMS Project Approval Drawer */}
      {selectedProjectId && !showAmsView && (
        <ProjectApprovalDrawer
          open={projectApprovalOpen}
          onClose={() => {
            setProjectApprovalOpen(false);
            setSelectedProjectId(null);
          }}
          projectId={selectedProjectId}
          onApprovalComplete={() => isApprover && loadPendingApprovals()}
        />
      )}

      {/* AMS Project Approval Drawer */}
      {selectedAmsProjectId && showAmsView && (
        <AmsAuditApprovalDrawer
          open={amsProjectApprovalOpen}
          onClose={() => {
            setAmsProjectApprovalOpen(false);
            setSelectedAmsProjectId(null);
            setSelectedAmsAuditId(null);
          }}
          projectId={selectedAmsProjectId}
          initialTab="audits"
          initialAuditId={selectedAmsAuditId}
          onApprovalComplete={() => {
            setAmsRefreshSignal((n) => n + 1);
          }}
        />
      )}
    </div>
  );
};

export default ProjectApprovals;
